import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaLink } from '@react-icons/all-files/fa/FaLink';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import PageForm, { FormField } from '../../../../commons/PageForm';
import UsuariosFilter from '../../../../commons/UsuariosFilter';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, updateEntity, reset } from '../../../../manager/workflow-empresa-actividad.reducer';

const BACK = '/configuracion/workflow/workflow-empresa-actividad';

// ── User selector field ───────────────────────────────────────────────────────

function UserSelectorField({ label, value, onChange }: { label: string; value: any; onChange: (u: any) => void }) {
  return (
    <div style={f.fieldGroup}>
      <label style={f.label}>{label}</label>
      <UsuariosFilter value={value} onChange={onChange} placeholder={`Buscar ${label.toLowerCase()}…`} />
    </div>
  );
}

const f: Record<string, React.CSSProperties> = {
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  divider: {
    borderTop: '1px solid #f1f5f9',
  },
};

interface EAForm {
  id: string;
  empresa: string;
  workflow: string;
  actividad: string;
  habilitado: string;
  requiereAutorizacion: string;
}

const empty: EAForm = { id: '', empresa: '', workflow: '', actividad: '', habilitado: 'true', requiereAutorizacion: 'true' };

export default function WorkflowEmpresaActividadFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.workflowEmpresaActividad.entity) as any;
  const updating = useAppSelector(s => s.workflowEmpresaActividad.updating);
  const updateSuccess = useAppSelector(s => s.workflowEmpresaActividad.updateSuccess);
  const errorMessage = useAppSelector(s => s.workflowEmpresaActividad.errorMessage);

  const [form, setForm] = useState<EAForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof EAForm, string>>>({});
  const [usuario, setUsuario] = useState<any>(null);
  const [autoriza, setAutoriza] = useState<any>(null);

  const { alert, showToast } = usePageAlert();

  // Always edit mode — no "new" for this entity
  useEffect(() => {
    if (id) dispatch(getEntity(id));
    return () => { dispatch(reset()); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate form from loaded entity
  useEffect(() => {
    if (entity?.id) {
      const emp = entity.workflowEmpresa?.empresa;
      const wf = entity.workflowEmpresa?.workflow
        ?? entity.profaseWorkflowEmpresa?.workflow;
      const act = entity.profaseWorkflowActividad ?? entity.workflowActividad;

      setForm({
        id: String(entity.id),
        empresa: emp ? `${emp.razonSocial ?? emp.nombre ?? ''} (${emp.rfc ?? ''})` : '',
        workflow: wf ? wf.nombre ?? '' : '',
        actividad: act ? act.nombre ?? '' : '',
        habilitado: String(entity.habilitado ?? true),
        requiereAutorizacion: String(entity.requiereAutorizacion ?? true),
      });
      setUsuario(entity.usuario ?? null);
      setAutoriza(entity.autoriza ?? null);
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate back on success
  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, { state: { successMsg: 'Registro actualizado correctamente.' } });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show error toast
  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fields: FormField<EAForm>[] = useMemo(() => [
    { name: 'id', label: 'ID', readOnly: true },
    { name: 'empresa', label: 'Empresa', readOnly: true },
    { name: 'workflow', label: 'Workflow', readOnly: true },
    { name: 'actividad', label: 'Actividad', readOnly: true },
    { name: 'habilitado', label: 'Habilitado', type: 'checkbox' },
    { name: 'requiereAutorizacion', label: 'Requiere Autorización', type: 'checkbox' },
  ], []);

  const handleFieldChange = (name: keyof EAForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload: any = {
      ...entity,
      habilitado: form.habilitado === 'true',
      usuario,
      autoriza,
      requiereAutorizacion: form.requiereAutorizacion === 'true'
    };

    dispatch(updateEntity(payload));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Workflow Empresa Actividad', to: BACK, icon: <FaLink /> },
        { label: `Editar #${id}` },
      ]} />

      <PageHeader
        icon={<FaLink />}
        iconColor="#84cc16"
        title={`Editar Actividad #${id}`}
        subtitle="Modificar datos de la actividad de empresa"
      />

      {alert && <PageAlert alert={alert} />}

      {/* ── User selectors ───────────────────────────────────────────── */}
      <div style={f.card}>
        <UserSelectorField label="Usuario" value={usuario} onChange={setUsuario} />
        <div style={f.divider} />
        {form.requiereAutorizacion === 'true' && <UserSelectorField label="Autoriza" value={autoriza} onChange={setAutoriza} />}
      </div>

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={false}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#84cc16"
      />
    </div>
  );
}
